import { useState, useEffect, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import type { LinkableContactResponse } from "../../../types/crm";
import {
  startSlackChannel,
  linkSlackChannel,
  getSlackWorkspaceInviteUrl,
} from "../../../services/crmConnectors";
import { sendContactMessage } from "../../../services/crmContacts";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import "./NewConvoModal.css";
import "./SlackConvoModal.css";

interface SlackConvoModalProps {
  isOpen: boolean;
  prospectProfileId: string;
  linkedParents: LinkableContactResponse[];
  onClose: () => void;
  onSuccess: () => void;
}

type Mode = "new" | "link";

export const SlackConvoModal = ({
  isOpen,
  prospectProfileId,
  linkedParents,
  onClose,
  onSuccess,
}: SlackConvoModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: Boolean(isOpen), dialogRef, handleEscape: false });

  const [mode, setMode] = useState<Mode>("new");
  const [message, setMessage] = useState("");
  const [channelUrl, setChannelUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ channelId: string; messageSent: boolean; skipped: string[]; inviteUrl: string | null } | null>(null);

  const parentsWithSlack = linkedParents.filter((p) => p.hasSlack);
  const [selectedParentIds, setSelectedParentIds] = useState<string[]>(
    () => parentsWithSlack.filter((p) => p.isInWorkspace).map((p) => p.contactId)
  );

  useEffect(() => {
    if (!isOpen) {
      setMode("new");
      setMessage("");
      setChannelUrl("");
      setSubmitting(false);
      setError("");
      setResult(null);
      setSelectedParentIds(parentsWithSlack.filter((p) => p.isInWorkspace).map((p) => p.contactId));
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !result) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, result]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      if (mode === "new") {
        const resp = await startSlackChannel(prospectProfileId, {
          initialMessage: message.trim() || undefined,
          parentContactIds: selectedParentIds.length > 0 ? selectedParentIds : undefined,
        });

        let inviteUrl: string | null = null;
        if (resp.skippedEmails.length > 0) {
          try {
            const { url } = await getSlackWorkspaceInviteUrl();
            inviteUrl = url || null;
          } catch {
            // non-critical
          }
        }

        onSuccess(); // refresh timeline in background
        setResult({
          channelId: resp.channelId,
          messageSent: resp.messageSent,
          skipped: resp.skippedEmails,
          inviteUrl,
        });
        setSubmitting(false);
      } else {
        if (!channelUrl.trim()) {
          setError("Please paste the Slack channel URL.");
          setSubmitting(false);
          return;
        }

        await linkSlackChannel(prospectProfileId, channelUrl.trim());

        if (message.trim()) {
          await sendContactMessage("ProspectProfile", prospectProfileId, {
            channel: "Slack",
            body: message.trim(),
          });
        }

        onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  // Result screen — shown after successful channel creation
  if (result) {
    return (
      <div className="new-convo-modal-overlay">
        <div ref={dialogRef} tabIndex={-1} className="new-convo-modal" role="dialog" aria-modal="true">
          <div className="new-convo-modal__header">
            <h2 className="new-convo-modal__title">Slack Channel Created</h2>
            <ModalCloseButton onClick={onClose} />
          </div>
          <div className="new-convo-modal__body">
            <div className="slack-convo-modal__result-row">
              {result.messageSent
                ? "Channel created and initial message sent."
                : "Channel created. No initial message was sent."}
            </div>
            {result.skipped.length > 0 && (
              <div className="slack-convo-modal__pending-callout">
                <div className="slack-convo-modal__pending-title">
                  {result.skipped.join(", ")} {result.skipped.length === 1 ? "isn't" : "aren't"} in your Slack workspace yet
                </div>
                <div className="slack-convo-modal__pending-body">
                  Share this workspace invite link with them.
                  {result.skipped.some(e => e.includes("@"))
                    ? " They'll be automatically added to the channel when they join."
                    : " Once they join, manually invite them to the channel in Slack."}
                </div>
                {result.inviteUrl && (
                  <div className="slack-convo-modal__invite-row">
                    <input
                      className="slack-convo-modal__invite-input"
                      type="text"
                      readOnly
                      value={result.inviteUrl}
                    />
                    <button
                      type="button"
                      className="slack-convo-modal__copy-btn"
                      onClick={() => navigator.clipboard.writeText(result.inviteUrl!)}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="new-convo-modal__footer">
            <button className="new-convo-modal__send" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="new-convo-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} tabIndex={-1} className="new-convo-modal" role="dialog" aria-modal="true" aria-label="Start Slack Conversation">
        <div className="new-convo-modal__header">
          <h2 className="new-convo-modal__title">Start Slack Conversation</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <form className="new-convo-modal__body" onSubmit={handleSubmit}>
          <div className="new-convo-modal__field">
            <span className="new-convo-modal__label" id="slack-convo-modal-mode-0">Mode</span>
            <div className="new-convo-modal__channel-tabs" role="group" aria-labelledby="slack-convo-modal-mode-0">
              <button
                type="button"
                className={`new-convo-modal__channel-btn ${mode === "new" ? "new-convo-modal__channel-btn--active" : ""}`}
                onClick={() => setMode("new")}
              >
                New channel
              </button>
              <button
                type="button"
                className={`new-convo-modal__channel-btn ${mode === "link" ? "new-convo-modal__channel-btn--active" : ""}`}
                onClick={() => setMode("link")}
              >
                Link existing channel
              </button>
            </div>
          </div>

          {mode === "link" && (
            <div className="new-convo-modal__field">
              <label htmlFor="slack-convo-modal-slack-channel-url-0" className="new-convo-modal__label">Slack channel URL</label>
              <input id="slack-convo-modal-slack-channel-url-0"
                className="new-convo-modal__input"
                type="text"
                value={channelUrl}
                onChange={(e) => setChannelUrl(e.target.value)}
                placeholder="https://yourworkspace.slack.com/archives/C12345678"
              />
            </div>
          )}

          {mode === "new" && parentsWithSlack.length > 0 && (
            <div className="new-convo-modal__field">
              <span className="new-convo-modal__label" id="slack-convo-modal-invite-parents-1">Invite parents</span>
              <div className="slack-convo-modal__parent-checkboxes" role="group" aria-labelledby="slack-convo-modal-invite-parents-1">
                {parentsWithSlack.map((p) => (
                  <label
                    key={p.contactId}
                    className={`slack-convo-modal__parent-checkbox-label${!p.isInWorkspace ? " slack-convo-modal__parent-checkbox-label--disabled" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedParentIds.includes(p.contactId)}
                      disabled={!p.isInWorkspace}
                      onChange={(e) =>
                        setSelectedParentIds((prev) =>
                          e.target.checked
                            ? [...prev, p.contactId]
                            : prev.filter((id) => id !== p.contactId)
                        )
                      }
                    />
                    <span>{p.displayName}</span>
                    {!p.isInWorkspace && (
                      <span className="slack-convo-modal__workspace-note">
                        Add them to your workspace to include them in this channel
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="new-convo-modal__field">
            <label htmlFor="slack-convo-modal-initial-message-optional-1" className="new-convo-modal__label">Initial message (optional)</label>
            <textarea id="slack-convo-modal-initial-message-optional-1"
              className="new-convo-modal__input new-convo-modal__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Write your first message..."
            />
          </div>

          {error && <div className="new-convo-modal__error">{error}</div>}

          <div className="new-convo-modal__footer">
            <button type="button" className="new-convo-modal__cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="new-convo-modal__send"
              disabled={submitting || (mode === "link" && !channelUrl.trim())}
            >
              {submitting
                ? "Setting up..."
                : mode === "new"
                ? "Create channel"
                : "Link channel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
